import React, { useEffect, useState, useCallback } from "react";
import ModalComponent from "../../../components/modals";
import LoadingFeedback, { useLoadingState } from "../../../components/loadingFeedback";
import { Grid } from "@mui/material";
import { callApiAsync, getHistoryData } from "../../../services/apiService";
import { getOptionName, requestState } from "../../../constants/selectData";
import SimpleSelect from "../../../components/selects/simpleSelect";
import { useForm } from "react-hook-form";
import LaddaButton from "../../../components/buttons/laddaButton";
import { icons } from "../../../constants";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { historyType, statesServiceOrderEnum } from "../../../constants/enums";
import PaginatedList from "../../../components/paginatedList";
import "./styles.css";
import "../filterStyles.css";
import { StyledTooltip } from "../../../components/toolltipComponent";
import MultiSelect from "../../../components/selects/multiSelect";
import { copyToClipboardWithFeedback } from "../../../utils/clipboardUtils";

const HistoryModal = ({ open, close, userId, isManagment, isViewer }) => {
  const [loadingHistory, setLoadingHistory] = useLoadingState();
  const [historyData, setHistoryData] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [osId, setOsId] = useState([]);
  const [serieId, setSerieId] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  const [allHistoryData, setAllHistoryData] = useState([]);
  // const [filteredHistory, setFilteredHistory] = useState([]);
  const [paginatedHistory, setPaginatedHistory] = useState([]);

  const itemsPerPage = 10;

  const { control, watch, setValue } = useForm();

  const formatDate = (dateString) => {
    return format(new Date(dateString), "d 'de' MMMM yyyy HH:mm", {
      locale: es
    });
  };

  const urlApi = process.env.REACT_APP_API_URL;

  const getHistory = useCallback(
    async (page = 1) => {
      await callApiAsync(
        () =>
          getHistoryData(userId, isViewer ? isViewer : isManagment, null, null),
        (data) => {
          const historyData = data.data;

          setAllHistoryData(historyData);
          setFilteredHistory(historyData); // inicial sin filtros
          setCurrentPage(1);

          setHistoryData(historyData);
          // setFilteredHistory(historyData);
          setTotalPages(data.totalPages);

          const uniqueOsIds = Array.from(
            new Map(
              historyData.map((item) => [
                item.serviceOrderName,
                { name: item.serviceOrderName, value: item.serviceOrderName }
              ])
            ).values()
          );

          const uniqueSeries = Array.from(
            new Set(historyData.flatMap((item) => item.series))
          ).map((serie) => ({
            name: serie,
            value: serie
          }));

          setOsId(uniqueOsIds);
          setSerieId(uniqueSeries);
        },
        setLoadingHistory
      );
    },
    [userId, isViewer, isManagment, setLoadingHistory]
  );

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    getHistory(newPage);
  };

  const copyDataInClipBoard = async (data, event) => {
    await copyToClipboardWithFeedback(
      data,
      () => setShowCopiedToast(true),
      () => console.error("Error al copiar al portapapeles"),
      event
    );
  };

  const handleFilter = () => {
    const selectedOsId = watch("osId");
    const selectedSerieId = watch("serieId");

    let filtered = allHistoryData;

    if (selectedOsId) {
      filtered = filtered.filter(
        (record) => record.serviceOrderName === selectedOsId
      );
    }

    if (selectedSerieId) {
      filtered = filtered.filter((record) =>
        record.series.includes(selectedSerieId)
      );
    }

    setFilteredHistory(filtered);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setValue("osId", "");
    setValue("serieId", "");
    setFilteredHistory(historyData);
  };

  useEffect(() => {
    if (open) {
      getHistory(0);
      setCurrentPage(1);
    }
  }, [open, getHistory]);

  useEffect(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setPaginatedHistory(filteredHistory.slice(start, end));
    setTotalPages(Math.ceil(filteredHistory.length / itemsPerPage));
  }, [filteredHistory, currentPage]);

  const renderHistoryItem = (record) => {
    switch (record.history_type) {
      case historyType.createOrder:
        return (
          <div className="history-item history-new">
            <strong className="history-os">{record.serviceOrderName}</strong>
            <div className="ml-10 IHContainer datac-history j-space-between">
              <div className="VContainer">
                <div className="history-user">
                  <strong>{record.userName}</strong> creo la nueva orden de
                  Servicio
                </div>
              </div>
              <div className="VContainer align-end">
                <span className="dateText-history">
                  {formatDate(record.creationDate)}
                </span>
                {record.comment == "" ? (
                  <></>
                ) : (
                  <StyledTooltip
                    title={
                      <>
                        <div>
                          <p className="toolkit-title">Comentario:</p>
                          <p className="toolkit-content">{record.comment}</p>
                        </div>
                      </>
                    }
                    followCursor
                    disableInteractive
                  >
                    <div
                      className="comment-copy-button j-center align-center pointer ml-10"
                      onClick={(event) => {
                        copyDataInClipBoard(record.comment, event);
                      }}
                      style={{
                        width: "30px",
                        height: "30px",
                        backgroundColor: "white",
                        borderRadius: "4px",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)"
                      }}
                    >
                      <img
                        className="btn-icon"
                        src={icons.so_comment}
                        alt="Copiar comentario"
                        style={{ width: "16px", height: "16px" }}
                      />
                    </div>
                  </StyledTooltip>
                )}
              </div>
            </div>
          </div>
        );

      case historyType.changeStateOrder:
        const isTestingState =
          String(record.newState) === String(statesServiceOrderEnum.Testing);

        const isReturnState =
          String(record.newState) === String(statesServiceOrderEnum.Return);

        const isFinalState =
          String(record.newState) === String(statesServiceOrderEnum.Completed);

        return (
          <div className="history-item history-changeState">
            <strong className="history-os">{record.serviceOrderName}</strong>
            <div className="ml-10 IHContainer datac-history j-space-between">
              <div className="VContainer j-center">
                <div className="history-user">
                  <strong>{record.userName}</strong> ha actualizado el estado
                </div>
                <div className="history-status-container">
                  <>
                    <span className="history-status">
                      {getOptionName(requestState, record.newState)}
                    </span>
                  </>
                </div>
              </div>
              <div className="VContainer align-end">
                <span className="dateText-history">
                  {formatDate(record.creationDate)}
                </span>

                {isTestingState || isReturnState ? (
                  <div
                    className="comment-copy-button j-center align-center pointer ml-10"
                    onClick={() => {
                      const path = record.image_path;
                      const fileUrlExample = `${urlApi}/${path}`;
                      window.open(fileUrlExample, "_blank");
                    }}
                    style={{
                      width: "30px",
                      height: "30px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)"
                    }}
                  >
                    <img
                      className="btn-icon"
                      src={icons.orangeFormGoogle}
                      alt={
                        isTestingState
                          ? "Ver archivo de prueba"
                          : "Ver archivo de retorno"
                      }
                      style={{ width: "16px", height: "16px" }}
                    />
                  </div>
                ) : record.comment == "" || isFinalState ? (
                  <></>
                ) : (
                  <StyledTooltip
                    title={
                      <div>
                        <p className="toolkit-title">Comentario:</p>
                        <p className="toolkit-content">{record.comment}</p>
                      </div>
                    }
                    followCursor
                  >
                    <div
                      className="comment-copy-button j-center align-center pointer ml-10"
                      onClick={() => {
                        copyDataInClipBoard(record.comment);
                      }}
                      style={{
                        width: "30px",
                        height: "30px",
                        backgroundColor: "white",
                        borderRadius: "4px",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)"
                      }}
                    >
                      <img
                        className="btn-icon"
                        src={icons.so_comment}
                        alt="Copiar comentario"
                        style={{ width: "16px", height: "16px" }}
                      />
                    </div>
                  </StyledTooltip>
                )}
              </div>
            </div>
          </div>
        );

      case historyType.changeEngineerOrder:
        return (
          <div className="history-item history-engineerChange">
            <strong className="history-os">{record.serviceOrderName}</strong>
            <div className="ml-10 IHContainer datac-history j-space-between">
              <div className="VContainer j-center">
                <div className="history-user">
                  <strong>{record.userName}</strong> cambio ingeniero a cargo:
                </div>
                <div className="history-status-container">
                  <span className="history-status">
                    {record.currentEngineerName}
                  </span>
                  <span className="history-arrow">→</span>
                  <span className="history-status">
                    {record.newEngineerName}
                  </span>
                </div>
              </div>
              <div className="VContainer align-end">
                <span className="dateText-history">
                  {formatDate(record.creationDate)}
                </span>
                {record.comment == "" ? (
                  <></>
                ) : (
                  <StyledTooltip
                    title={
                      <>
                        <div>
                          <p className="toolkit-title">Comentario:</p>
                          <p className="toolkit-content">{record.comment}</p>
                        </div>
                      </>
                    }
                    followCursor
                    disableInteractive
                  >
                    <div
                      className="comment-copy-button j-center align-center pointer ml-10"
                      onClick={(event) => {
                        copyDataInClipBoard(record.comment, event);
                      }}
                      style={{
                        width: "30px",
                        height: "30px",
                        backgroundColor: "white",
                        borderRadius: "4px",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)"
                      }}
                    >
                      <img
                        className="btn-icon"
                        src={icons.so_comment}
                        alt="Copiar comentario"
                        style={{ width: "16px", height: "16px" }}
                      />
                    </div>
                  </StyledTooltip>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="history-item">
            <div className="mr-20" style={{ minWidth: "55px" }}>
              <strong className="history-os">{record.serviceOrderName}</strong>
            </div>
            <div className="VContainer j-center">
              <div className="history-user">
                <strong>{record.userName}</strong> realizó una acción
                desconocida.
              </div>
            </div>
          </div>
        );
    }
  };

  const handleClose = () => {
    handleClearFilters();
    close();
  };

  return (
    <>
      <ModalComponent
        open={open}
        handleClose={handleClose}
        title={"Historial"}
        width="800px"
        maxWidth="calc(100vw - 32px)"
        actions={<></>}
      >
        <Grid container spacing={2} alignItems="flex-end" className="biomedical-filters" sx={{ mt: 0, mb: 2 }}>
          <Grid item xs={12} sm={5}>
            <SimpleSelect
              name="osId"
              control={control}
              label="Nro. de OS"
              options={osId}
              margin={"0"}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <MultiSelect
              name="serieId"
              control={control}
              label="Nro. de serie"
              options={serieId}
              margin={"0"}
              // error={errors.serie}
              isMultiple={false}
              searchBar={true}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <LaddaButton
                variant="contained"
                onClick={handleFilter}
                margin={"0"}
                width="40px"
                height="40px"
                aria-label="Filtrar historial"
              >
                <img
                  className="btn-icon"
                  src={icons.filterWhite}
                  alt="Filtrar"
                />
              </LaddaButton>
              <LaddaButton
                variant="contained"
                onClick={handleClearFilters}
                margin={"0"}
                width="40px"
                height="40px"
                aria-label="Limpiar filtros"
              >
                <img
                  className="btn-icon"
                  src={icons.clearFilterWhite}
                  alt="Limpiar filtros"
                />
              </LaddaButton>
            </div>
          </Grid>
        </Grid>

        <LoadingFeedback loading={loadingHistory} rows={4}>
        {filteredHistory.length === 0 ? (
          <p>No hay historial disponible</p>
        ) : (
          <div className="history-container">
            {filteredHistory.map((record, index) => (
              <React.Fragment key={index}>
                {renderHistoryItem(record)}
              </React.Fragment>
            ))}
          </div>
        )}

        <PaginatedList
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
        </LoadingFeedback>
      </ModalComponent>
    </>
  );
};

export default HistoryModal;
